import translate from '@allesauto/i18n';
import {
  SCREEN_BREAKPOINT,
  useOnClickOutside,
  useScreenBreakpoint,
} from '@allesauto/utils/hooks';
import { ChevronDown20 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, {
  Children,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import StepItem, { type StepItemProps } from './StepItem';
import ConditionalWrapper from '../ConditionalWrapper';

export type { StepItemProps };

export interface StepIndicatorProps extends React.PropsWithChildren<any> {
  currentStep: number;
  isOpen?: boolean;
}

const StepIndicator = ({
  currentStep,
  children,
  isOpen = false,
}: React.PropsWithChildren<StepIndicatorProps>): JSX.Element => {
  const intl = useIntl();

  const elRef = useRef(null);
  const listRef = useRef(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const onStepItemClickRef = useRef<any>(null);
  const onStepItemClickRefEvent = useRef<React.MouseEvent | null>(null);

  const [isOpenState, setIsOpenState] = useState<boolean>(isOpen);
  const [currentStepState, setCurrentStepState] = useState<number>(currentStep);
  const [childrenCount, setChildrenCount] = useState<number>(0);
  const [isSmallBreakpoint, setIsSmallBreakpoint] = useState(false);

  const TRANSITION_END = 300;

  useOnClickOutside(elRef, () => setIsOpenState(false));

  useEffect(() => {
    setChildrenCount(Children.toArray(children).length - 1);
  }, [children]);

  useEffect(() => {
    setCurrentStepState(Math.max(0, Math.min(currentStep, childrenCount)));
  }, [childrenCount, currentStep, currentStepState]);

  const currentBreakpoint = useScreenBreakpoint();

  useEffect(() => {
    setIsSmallBreakpoint(currentBreakpoint <= SCREEN_BREAKPOINT.SM);
  }, [currentBreakpoint]);

  const classNames = {
    Container: clsx(
      'bg-white grid grid-cols-24 justify-center min-h-[51px] relative sm:z-[45]',
      !isSmallBreakpoint &&
        'after:absolute after:bg-grey30 after:block after:bottom-0 after:content-[""] after:h-[3px] after:right-0 after:w-1/2 after:z-0',
      !isSmallBreakpoint &&
        'before:absolute before:bg-green70 monochrome:before:bg-black before:block before:bottom-0 before:content-[""] before:h-[3px] before:left-0 before:w-1/2 before:z-0',
      'wl-progress-bar-color'
    ),
    Base: clsx(
      'grid relative',
      isSmallBreakpoint ? 'col-start-1 col-end-25' : 'col-start-2 col-end-24'
    ),
    GridFlow: clsx(isSmallBreakpoint ? 'grid-flow-row' : 'grid-flow-col'),
  };

  const toggleOpenCloseState = () => {
    setIsOpenState(!isOpenState);
  };

  const currentStepProps = useMemo((): StepItemProps | null => {
    let stepProps: StepItemProps | null = null;

    Children.forEach(children, (child: any, childIndex: number) => {
      if (childIndex === currentStepState)
        stepProps = child.props as StepItemProps;
    });

    return stepProps;
  }, [currentStepState, children]);

  const getOverallPercentage = useCallback(() => {
    const childCount = Children.toArray(children).length - 1;
    return Math.min(100, (100 / childCount) * (currentStepState + 0.5));
  }, [currentStepState, children]);

  useEffect(() => {
    if (listRef && listRef.current && overlayRef && overlayRef.current) {
      let height = 0;
      let opacity = '0';
      let heightOverlay = '0';
      const listElement = listRef.current as HTMLLIElement;
      const overlayElement = overlayRef.current;

      if (isSmallBreakpoint && isOpenState) {
        opacity = '1';
        heightOverlay = '100%';
        listElement.style.height = 'auto';
        listElement.style.overflow = 'visible';
        listElement.style.display = 'block';
        listElement.style.opacity = '0';
        height = listElement.getBoundingClientRect().height;
        listElement.removeAttribute('style');
        listElement.style.height = '0px';

        overlayElement.style.height = heightOverlay;
        overlayElement.style.opacity = opacity;
      }

      overlayElement.style.opacity = opacity;

      window.setTimeout(() => {
        listElement.style.height = height + 'px';
      }, 25);

      window.setTimeout(() => {
        overlayElement.style.height = heightOverlay;
      }, TRANSITION_END);
    }
  }, [isOpenState, isSmallBreakpoint, listRef]);

  useEffect(() => {
    setIsOpenState(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const listEl =
      listRef && listRef.current ? (listRef.current as HTMLLIElement) : null;

    if (listEl && isSmallBreakpoint) {
      listEl.addEventListener('transitionend', onListTransitionEnd);
    }

    return () => {
      if (listEl) {
        listEl.removeEventListener('transitionend', onListTransitionEnd);
      }
    };
  }, [listRef, isSmallBreakpoint]);

  const onListTransitionEnd = (event: TransitionEvent) => {
    if (
      event.propertyName === 'height' &&
      onStepItemClickRef &&
      onStepItemClickRef.current &&
      typeof onStepItemClickRef.current === 'function' &&
      onStepItemClickRefEvent &&
      onStepItemClickRefEvent.current
    ) {
      (onStepItemClickRef.current as (event: React.MouseEvent) => void)(
        onStepItemClickRefEvent.current as React.MouseEvent
      );
    }
  };

  return (
    <>
      {isSmallBreakpoint && (
        <div
          ref={overlayRef}
          className="absolute bg-grey100 bg-opacity-50 duration-300 ease-in-back h-0 inset-0 opacity-0 transition-opacity z-[45]"
        ></div>
      )}
      <div className={classNames.Container}>
        <ol
          className={clsx(
            classNames.Base,
            classNames.GridFlow,
            isSmallBreakpoint &&
              'after:absolute after:bg-grey30 after:block after:bottom-0 after:content-[""] after:h-[3px] after:left-0 after:w-full after:z-1'
          )}
          onClick={toggleOpenCloseState}
          ref={elRef}
        >
          {isSmallBreakpoint && (
            <>
              <StepItem
                isDropdownDisplay={true}
                label={translate(intl, 'ui.STEP_INDICATOR.STEP_N_OF_N', {
                  current: Math.max(1, currentStepState + 1),
                  max: Children.toArray(children).length || 0,
                  stepLabel: currentStepProps?.label ?? '',
                })}
                className="cursor-pointer"
                disableNavigation={currentStepProps?.disableNavigation}
              />
              <ChevronDown20
                aria-hidden="true"
                className={clsx(
                  'absolute duration-300 ease-out-back right-[15px] text-purple80 top-[50%] transform-gpu transition-rotate translate-y-[-50%] wl-color monochrome:text-grey100',
                  isOpenState ? '-rotate-180' : 'rotate-0',
                  !isSmallBreakpoint && 'hidden'
                )}
              />
            </>
          )}
          <ConditionalWrapper
            condition={isSmallBreakpoint}
            wrapper={(children: any) => {
              return (
                <li
                  ref={listRef}
                  className={clsx(
                    'absolute bg-white left-0 overflow-hidden top-[100%] w-full z-50',
                    'duration-300 ease-in-back transition-height'
                  )}
                >
                  <ol className="bg-white">{children}</ol>
                </li>
              );
            }}
          >
            <>
              {Children.map(
                children as unknown as any,
                (child: ReactElement, index: number) => {
                  return React.cloneElement(child, {
                    key: `StepIndicator-StepItem-${index}`,
                    currentStep: currentStepState,
                    itemIndex: index,
                    isSmallBreakpoint,
                  });
                }
              )}
            </>
          </ConditionalWrapper>
          {isSmallBreakpoint && (
            <span
              aria-hidden="true"
              style={{
                width: getOverallPercentage() + '%',
              }}
              className="absolute bg-green70 block bottom-0 h-[3px] left-0 pointer-events-none top-100 wl-bg-highlight z-20 monochrome:bg-grey100"
            />
          )}
        </ol>
      </div>
    </>
  );
};

export { StepItem };

export default StepIndicator;
